import { useId, useState } from "react";

import type { DeliverableKind, Id } from "../../../api/types";
import { Icon } from "../../../ui";
import { SheetIcon } from "../../../ui/SheetIcon";

/** A kind of document that can read a field: the picker offers these and nothing else. */
export interface DeliverableChoice {
  id: Id;
  name: string;
  letter?: DeliverableKind | null;
}

export interface NeededByPickerProps {
  /** What can be picked: the item's own deliverables. */
  choices: readonly DeliverableChoice[];
  /** Deliverables that are picked already but are not among the choices (named for the chips only). */
  known?: readonly DeliverableChoice[];
  /** The ids picked. */
  value: readonly Id[];
  onChange?: (value: Id[]) => void;
  /** The free text an older version of the field was given (NB-01). Shown as a note while nothing is picked. */
  legacyText?: string | null;
  readOnly?: boolean;
}

/**
 * "Needed by" as a pick: one chip for each deliverable that reads the field, and a "+" that offers the item's other
 * deliverables. Nothing is typed. A field that only has the old free-text line shows it as a note, so nothing is lost.
 */
export function NeededByPicker({ choices, known = [], value, onChange, legacyText, readOnly = false }: NeededByPickerProps) {
  const selectId = useId();
  const [adding, setAdding] = useState(false);
  const byId = new Map<Id, DeliverableChoice>([...known, ...choices].map((entry) => [entry.id, entry] as const));
  const rest = choices.filter((entry) => !value.includes(entry.id));
  const note = legacyText?.trim();

  return (
    <div className="needed-by-picker">
      {value.length === 0 && !adding && <span className="muted">{note ? "No deliverable picked yet" : "Not named yet"}</span>}
      {value.map((id) => {
        const entry = byId.get(id);
        const name = entry?.name ?? "Another deliverable";
        return (
          <span key={id} className="chip needed-by-picker__chip">
            {entry?.letter && <SheetIcon kind={entry.letter} size={14} />} {name}
            {!readOnly && (
              <button type="button" className="needed-by-picker__remove" aria-label={`Remove ${name}`} onClick={() => onChange?.(value.filter((other) => other !== id))}>
                <Icon name="x" size={10} />
              </button>
            )}
          </span>
        );
      })}
      {!readOnly &&
        (adding ? (
          <>
            <label htmlFor={selectId} className="sr-only">
              Add a deliverable
            </label>
            <select
              id={selectId}
              className="field__input needed-by-picker__select"
              value=""
              autoFocus
              onChange={(event) => {
                if (event.target.value) onChange?.([...value, event.target.value]);
                setAdding(false);
              }}
              onBlur={() => setAdding(false)}
            >
              <option value="">Pick a deliverable</option>
              {rest.map((entry) => (
                <option key={entry.id} value={entry.id}>
                  {entry.name}
                </option>
              ))}
            </select>
          </>
        ) : (
          <button
            type="button"
            className="chip needed-by-picker__add"
            aria-label="Add a deliverable"
            disabled={rest.length === 0}
            title={rest.length === 0 ? (choices.length === 0 ? "This item has no deliverables yet" : "Every deliverable of this item is picked") : undefined}
            onClick={() => setAdding(true)}
          >
            <Icon name="plus" size={10} />
          </button>
        ))}
      {note && value.length === 0 && <p className="needed-by-picker__note muted">Written before the pick existed: {note}</p>}
    </div>
  );
}
